<template>
  <div>
    <transition name="fade">
      <div
        v-if="visible"
        class="fixed inset-0 z-30 bg-black/50 lg:hidden"
        data-testid="sidebar-overlay"
        @click="$emit('close')"
      ></div>
    </transition>

    <aside
      class="app-sidebar fixed inset-y-0 left-0 z-40 hbox w-64 flex-col transition-all duration-300"
      :class="[{ 'is-visible': visible }, collapsed ? 'lg:w-16' : 'lg:w-64']"
      data-testid="sidebar"
    >
      <!--
        Marca + colapsar. El mismo botón: a la derecha si está abierto,
        arriba y centrado si está contraído. span, no p: Vuetify margina los p.
      -->
      <div class="sidebar-brand hbox shrink-0 items-center gap-3" :class="collapsed ? 'is-collapsed lg:flex-col lg:gap-2' : ''">
        <img class="sidebar-logo" src="@/assets/imgs/rm.svg" alt="Automotriz RM" />
        <div v-show="!collapsed || visible" class="min-w-0 flex-1">
          <span class="sidebar-name">Automotriz RM</span>
          <span class="sidebar-caption">GESTIÓN DE TALLER</span>
        </div>
        <button
          type="button"
          class="sidebar-icon-btn hidden lg:inline-flex"
          :class="collapsed ? 'order-first' : 'ml-auto'"
          data-testid="collapse-btn"
          :title="collapsed ? 'Expandir menú' : 'Contraer menú'"
          :aria-label="collapsed ? 'Expandir menú' : 'Contraer menú'"
          @click="$emit('toggle-collapse')"
        >
          <AppIcon :name="collapsed ? 'chevrons-right' : 'chevrons-left'" size="h-4 w-4" />
        </button>
        <button
          type="button"
          class="sidebar-icon-btn ml-auto lg:hidden"
          data-testid="sidebar-close-mobile"
          aria-label="Cerrar menú"
          @click="$emit('close')"
        >
          <AppIcon name="x-mark" />
        </button>
      </div>

      <nav ref="nav" class="sidebar-nav flex-1 space-y-0.5 overflow-y-auto px-3 py-2">
        <!--
          Secciones diarias: el título solo agrupa (no se pulsa). Contraído, el
          título se cambia por una línea para no perder la separación.
        -->
        <div v-for="(section, index) in sections" :key="section.id" class="space-y-0.5">
          <template v-if="index">
            <p
              v-if="section.label"
              v-show="!collapsed || visible"
              class="nav-section-label"
              :data-testid="'nav-section-' + section.id"
            >{{ section.label }}</p>
            <div
              v-show="!section.label || (collapsed && !visible)"
              class="nav-divider"
              :class="collapsed ? 'lg:mx-1' : ''"
            ></div>
          </template>
          <NavLink
            v-for="item in section.items"
            :key="item.name"
            dense
            class="nav-top lg:min-h-9"
            :class="collapsed ? 'lg:justify-center lg:px-0' : ''"
            :to="{ name: item.name }"
            :test-id="'nav-item-' + item.name"
            :title="collapsed ? item.label : ''"
            :active="isActiveItem(item)"
            :show-indicator="false"
            active-class="is-active"
            inactive-class=""
            @navigate="$emit('close')"
          >
            <AppIcon :name="item.icon" size="h-4 w-4" class="shrink-0" />
            <span v-show="!collapsed || visible" class="truncate">{{ item.label }}</span>
          </NavLink>
        </div>

        <div class="nav-divider" :class="collapsed ? 'lg:mx-1' : ''"></div>

        <div v-for="group in groups" :key="group.id">
          <button
            type="button"
            class="nav-group hbox min-h-11 w-full items-center lg:min-h-9 gap-2 rounded-lg px-2 text-base font-medium transition-colors"
            :class="[
              isOpen(group) || (collapsed && isGroupActive(group)) ? 'is-open' : '',
              isGroupActive(group) ? 'is-active' : '',
              collapsed ? 'lg:justify-center lg:px-0' : '',
            ]"
            :title="collapsed ? group.label : ''"
            :data-testid="'group-toggle-' + group.id"
            @click="toggleGroup(group)"
          >
            <AppIcon :name="group.icon" size="h-4 w-4" class="nav-icon shrink-0" />
            <span v-show="!collapsed || visible" class="truncate">{{ group.label }}</span>
            <AppIcon
              v-show="!collapsed || visible"
              name="chevron-down"
              size="h-3.5 w-3.5"
              class="nav-chevron ml-auto transition-transform duration-200"
              :class="isOpen(group) ? 'rotate-180' : ''"
            />
          </button>

          <div class="submenu" :class="{ 'submenu-open': isOpen(group) && (!collapsed || visible) }">
            <div class="submenu-inner">
              <div class="mb-1 mt-0.5 space-y-0.5 py-0.5 pl-3">
                <NavLink
                  v-for="item in group.items"
                  :key="item.name"
                  dense
                  class="lg:min-h-9"
                  :to="{ name: item.name }"
                  :test-id="'nav-item-' + item.name"
                  :active="isActiveItem(item)"
                  :show-indicator="false"
                  active-class="is-active"
                  inactive-class=""
                  @navigate="$emit('close')"
                >
                  <AppIcon :name="item.icon" size="h-3.5 w-3.5" class="shrink-0 opacity-80" />
                  <span v-show="!collapsed || visible" class="truncate">{{ item.label }}</span>
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div
        class="sidebar-footer hbox shrink-0 items-center gap-2"
        :class="collapsed ? 'is-collapsed lg:flex-col lg:justify-center lg:gap-1' : ''"
      >
        <span class="sidebar-avatar hbox shrink-0 items-center justify-center rounded-full text-[10px] font-bold uppercase">
          {{ initials }}
        </span>
        <span v-show="!collapsed || visible" class="sidebar-user min-w-0 flex-1 truncate text-base font-medium leading-none" data-testid="sidebar-user">
          {{ userName }}
        </span>
        <button
          type="button"
          class="sidebar-icon-btn sidebar-logout"
          :title="collapsed ? 'Cerrar sesión' : ''"
          aria-label="Cerrar sesión"
          data-testid="logout-btn"
          @click="$emit('logout')"
        >
          <AppIcon name="logout" size="h-4 w-4" />
        </button>
      </div>
    </aside>
  </div>
</template>

<script>
/*
 * AppSidebar — menú lateral.
 *
 * Secciones diarias a un clic (título que no se pulsa) y submenús solo para
 * lo que casi no se toca. Opciones de 36px en escritorio (44px en móvil) para
 * que todo quepa sin scroll a 1280×720. Paleta: navy de fondo, texto claro en grupos. El submenú se distingue
 * por la sangría y el ítem activo usa el azul de marca.
 * Un acordeón: abrir un grupo cierra el otro, con animación de 180ms.
 * Colapsado: el botón de expandir queda arriba, centrado. Clic en un grupo
 * abre el menú y ese submenú.
 */
import { computed, getCurrentInstance, nextTick, onMounted, ref, watch } from 'vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import NavLink from './NavLink.vue'
import { groupForRoute, groups, sections } from './nav-items'

export default {
  name: 'AppSidebar',
  components: { AppIcon, NavLink },
  props: {
    visible: { type: Boolean, default: false },
    collapsed: { type: Boolean, default: false },
    currentName: { type: String, default: '' },
    userName: { type: String, default: 'Usuario' },
  },
  emits: ['close', 'toggle-collapse', 'logout', 'expand'],
  setup(props, { emit }) {
    const nav = ref(null)
    const openGroups = ref([])

    function isOpen(group) {
      return openGroups.value.includes(group.id)
    }

    function toggleGroup(group) {
      if (props.collapsed && !props.visible) {
        openGroups.value = [group.id]
        emit('expand')
        return
      }
      openGroups.value = isOpen(group) ? [] : [group.id]
    }

    watch(
      () => props.currentName,
      (name) => {
        const group = groupForRoute(name)
        openGroups.value = group ? [group.id] : []
      },
      { immediate: true }
    )

    const route = computed(() => getCurrentInstance().proxy.$route)

    function isActiveItem(item) {
      const matched = (route.value.matched || []).map((record) => record.name)
      return item.exact === false ? matched.includes(item.name) : route.value.name === item.name
    }

    const activeGroupId = computed(() => {
      const group = groups.find((g) => g.items.some((item) => isActiveItem(item)))
      return group ? group.id : null
    })

    function isGroupActive(group) {
      return activeGroupId.value === group.id
    }

    function scrollActiveIntoView() {
      nextTick(() => {
        const active = nav.value && nav.value.querySelector('[data-active="true"]')
        if (active && active.offsetParent !== null) active.scrollIntoView({ block: 'center' })
      })
    }

    onMounted(scrollActiveIntoView)
    watch(() => props.currentName, scrollActiveIntoView)
    watch(() => props.collapsed, (value) => {
      if (!value) scrollActiveIntoView()
    })

    const initials = computed(() => {
      const parts = (props.userName || 'U').trim().split(/\s+/)
      return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase()
    })

    return { groups, initials, isActiveItem, isGroupActive, isOpen, nav, sections, toggleGroup }
  },
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
/* Tailwind 4 usa translate individual, ignorado por Chromium 103 y anteriores.
 * La navegación debe abrir y cerrar también en esas tablets. */
.app-sidebar { transform: translateX(-100%); height: 100vh; height: 100dvh; border-right: 1px solid #173a5b; background: #102a43; color: #d9e5ef; font-family: Inter, Roboto, sans-serif; box-shadow: 8px 0 28px rgba(16, 42, 67, .08); }
.app-sidebar.is-visible { transform: translateX(0); }
.sidebar-brand { min-height: 84px; padding: 14px 14px; border-bottom: 1px solid rgba(255, 255, 255, .1); }
.sidebar-logo { width: 54px; height: 54px; flex: none; padding: 3px; border-radius: 12px; background: #fff; object-fit: contain; }
.sidebar-name { display: block; color: #fff; font-size: 13px; font-weight: 700; line-height: 1.15; white-space: nowrap; }
.sidebar-caption { display: block; margin-top: 5px; color: #99b5cc; font-size: 9px; font-weight: 700; letter-spacing: .14em; white-space: nowrap; }
.sidebar-icon-btn { display: inline-flex; width: 32px; height: 32px; flex: none; align-items: center; justify-content: center; border: 0; border-radius: 9px; background: transparent; color: #9bb4ca; cursor: pointer; }
.sidebar-icon-btn:hover { background: rgba(255,255,255,.1); color: #fff; }
.sidebar-icon-btn:focus-visible, .nav-group:focus-visible { outline: 3px solid #84b9e8; outline-offset: 2px; }
.sidebar-nav { min-height: 0; }
.nav-section-label { margin: 8px 11px 4px; color: #88a8c1; font-size: 10px; font-weight: 800; letter-spacing: .16em; text-transform: uppercase; }
.nav-divider { height: 1px; margin: 8px 10px; background: rgba(255,255,255,.1); }
.nav-group { color: #d0dfeb; }
.nav-group:hover, .nav-group.is-open { background: rgba(255,255,255,.08); color: #fff; }
.nav-group.is-active { color: #fff; }
.nav-icon, .nav-chevron { color: #91acc2; }
.nav-group.is-open .nav-icon, .nav-group.is-active .nav-icon { color: #a9d4fa; }
.sidebar-footer { min-height: 58px; padding: 8px 14px; border-top: 1px solid rgba(255,255,255,.1); }
.sidebar-avatar { width: 32px; height: 32px; background: #dcecff; color: #0a4f9c; }
.sidebar-user { color: #f1f6fa; }
.sidebar-logout:hover { background: #fff1f2; color: #b91c1c; }
@media (max-width: 1023px) { .sidebar-brand [data-testid='collapse-btn'] { display: none; } }
@media (pointer: coarse), (max-width: 1023px) {
  .sidebar-icon-btn { width: 44px; height: 44px; }
  .sidebar-brand { gap: 8px; }
  .sidebar-logo { width: 44px; height: 44px; }
  .sidebar-nav .nav-top, .sidebar-nav .nav-group, .sidebar-nav .submenu a { min-height: 44px; }
}

.submenu {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  visibility: hidden;
  transition: grid-template-rows 180ms ease-out, opacity 180ms ease-out, visibility 0s linear 180ms;
}
.submenu-open {
  grid-template-rows: 1fr;
  opacity: 1;
  visibility: visible;
  transition: grid-template-rows 180ms ease-out, opacity 180ms ease-out, visibility 0s linear 0s;
}
.submenu-inner {
  min-height: 0;
  overflow: hidden;
}
@media (prefers-reduced-motion: reduce) { .submenu, .submenu-open, .fade-enter-active, .fade-leave-active { transition: none; } }
@media (min-width: 1024px) {
  .app-sidebar { transform: translateX(0); }
  .sidebar-brand [data-testid='sidebar-close-mobile'] { display: none; }
  .sidebar-brand.is-collapsed { padding-left: 8px; padding-right: 8px; }
  .sidebar-brand.is-collapsed .sidebar-logo { width: 36px; height: 36px; }
  .sidebar-footer.is-collapsed { min-height: 84px; padding: 8px; }
}
</style>

<!-- Sin scope: Vuetify pinta todo <a> con el azul de marca. -->
<style>
.v-application [data-testid='sidebar'] a {
  color: #d0dfeb;
  text-decoration: none;
}
.v-application [data-testid='sidebar'] a.nav-top:not([data-active='true']) {
  color: #d0dfeb;
}
.v-application [data-testid='sidebar'] a:hover {
  color: #fff;
  background: rgba(255,255,255,.09);
}
.v-application [data-testid='sidebar'] a[data-active='true'] {
  color: #fff;
  background-color: #1d5b93;
  font-weight: 700;
}
.v-application [data-testid='sidebar'] a:focus-visible {
  outline: 3px solid #84b9e8;
  outline-offset: 2px;
}
</style>
